import { useState, useEffect, useCallback } from "react";
import { HealthMemoryService } from "@/services/HealthMemoryService";
import {
  HealthMemoryResponseDto,
  CreateHealthMemoryDto,
  UpdateHealthMemoryDto,
} from "@/types/health-memory";
import {
  BackendFilter,
  FilterSettingDto,
  FilterOperation,
  FilterType,
} from "@/types/filter";

export const useHealthMemory = (memberId: string) => {
  const [healthMemories, setHealthMemories] = useState<
    HealthMemoryResponseDto[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const healthMemoryService = new HealthMemoryService(memberId);

  // Fetch all health memories with optional filtering
  const fetchHealthMemories = useCallback(
    async (filter?: {
      year?: number;
      search?: string;
      page?: number;
      pageSize?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const getAllFilter: FilterSettingDto = {
          filters: {
            memberId: {
              value: memberId,
              type: FilterType.STRING,
              operation: FilterOperation.EQUAL,
            },
          },
          sort: [{ field: "year", order: "desc" }],
          pagination: { page: 1, pageSize: 10 },
        };
        if (filter?.year) {
          getAllFilter.filters = {
            ...getAllFilter.filters,
            year: {
              value: String(filter.year),
              type: FilterType.NUMBER,
              operation: FilterOperation.EQUAL,
            },
          };
        }
        if (filter?.search) {
          getAllFilter.filters = {
            ...getAllFilter.filters,
            title: {
              value: filter.search,
              type: FilterType.STRING,
              operation: FilterOperation.CONTAINS,
            },
            details: {
              value: filter.search,
              type: FilterType.STRING,
              operation: FilterOperation.CONTAINS,
            },
          };
        }
        if (filter?.page) getAllFilter.pagination.page = filter.page;
        if (filter?.pageSize)
          getAllFilter.pagination.pageSize = filter.pageSize;
        const data = await healthMemoryService.getHealthMemories(getAllFilter);
        setHealthMemories(data.data);
        return data.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch health memories";
        setError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  // Get a single health memory by ID
  const fetchHealthMemory = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        const memory = await healthMemoryService.getHealthMemory(id);
        return memory;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : `Failed to fetch health memory with ID: ${id}`;
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  // Create a new health memory
  const createHealthMemory = useCallback(
    async (memoryData: CreateHealthMemoryDto) => {
      setLoading(true);
      setError(null);
      try {
        const newMemory = await healthMemoryService.createHealthMemory(
          memoryData
        );
        setHealthMemories((prev) => [...prev, newMemory]);
        return newMemory;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create health memory";
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  // Update an existing health memory
  const updateHealthMemory = useCallback(
    async (id: string, memoryData: UpdateHealthMemoryDto) => {
      setLoading(true);
      setError(null);
      try {
        const updatedMemory = await healthMemoryService.updateHealthMemory(
          id,
          memoryData
        );
        setHealthMemories((prev) =>
          prev.map((m) => (m.id === id ? updatedMemory : m))
        );
        return updatedMemory;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : `Failed to update health memory with ID: ${id}`;
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  // Delete a health memory
  const deleteHealthMemory = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        await healthMemoryService.deleteHealthMemory(id);
        setHealthMemories((prev) => prev.filter((m) => m.id !== id));
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : `Failed to delete health memory with ID: ${id}`;
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  return {
    healthMemories,
    loading,
    error,
    fetchHealthMemories,
    fetchHealthMemory,
    createHealthMemory,
    updateHealthMemory,
    deleteHealthMemory,
  };
};

export default useHealthMemory;
